import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { BulkRiskScoreResult, CompanyComparison } from '@marstein-radar/shared';
import {
  inputStyle,
  buttonStyle,
  tableStyle,
  thStyle,
  tdStyle,
  riskColor,
  formatEur,
} from '../styles';

// Nástroje pre pokročilé/B2B použitie — hromadné skórovanie a porovnanie firiem
// (Blok 3, Týž. 9+12). Vyhľadávanie osôb presunuté do /osoby (E-Analytika).
export function ToolsPage() {
  const navigate = useNavigate();
  const [bulkIcosText, setBulkIcosText] = useState('');
  const [bulkResults, setBulkResults] = useState<BulkRiskScoreResult[] | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  async function submitBulkScore(): Promise<void> {
    const icos = bulkIcosText
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (icos.length === 0) return;
    setBulkLoading(true);
    setBulkError(null);
    try {
      const res = await fetch('/api/v1/companies/bulk-risk-score', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ icos }),
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      setBulkResults(await res.json());
    } catch (err) {
      setBulkError((err as Error).message);
    } finally {
      setBulkLoading(false);
    }
  }

  // Týž. 19 — porovnanie 2-3 firiem vedľa seba. Týž. 27 (user feedback
  // 2026-07-19: checkboxy vo výsledkoch vyhľadávania → "Porovnať vybrané") —
  // ?compare=ico1,ico2 z /vyhladavanie predvyplní a rovno spustí porovnanie.
  const [searchParams] = useSearchParams();
  const [compareIcosText, setCompareIcosText] = useState(
    () => searchParams.get('compare')?.split(',').filter(Boolean).join(', ') ?? '',
  );
  const [compareResults, setCompareResults] = useState<CompanyComparison[] | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);

  async function submitCompare(icosOverride?: string[]): Promise<void> {
    const icos =
      icosOverride ??
      compareIcosText
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
    setCompareError(null);
    if (icos.length < 2) {
      setCompareError('Zadaj aspoň 2 IČO.');
      return;
    }
    if (icos.length > 3) {
      setCompareError('Max 3 IČO na porovnanie.');
      return;
    }
    setCompareLoading(true);
    try {
      const res = await fetch('/api/v1/companies/compare', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ icos }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message ?? `${res.status} ${res.statusText}`);
      }
      setCompareResults(await res.json());
    } catch (err) {
      setCompareError((err as Error).message);
    } finally {
      setCompareLoading(false);
    }
  }

  useEffect(() => {
    const icos = searchParams.get('compare')?.split(',').filter(Boolean) ?? [];
    if (icos.length >= 2 && icos.length <= 3) void submitCompare(icos);
    // Len pri mounte — `compare` v URL je jednorazový vstupný parameter
    // z /vyhladavanie, nie live-viazaný stav.
  }, []);

  return (
    <main className="page page-narrow">
      <div className="page-header">
        <h1>Nástroje</h1>
        <p>
          Hromadné rizikové skórovanie a porovnanie firiem. Vyhľadávanie osôb má vlastnú sekciu{' '}
          <a href="/osoby">Osoby</a>.
        </p>
      </div>

      {/* Vyhľadávanie osôb presunuté do samostatnej sekcie /osoby (E-Analytika,
          2026-07-18) — tu ostávajú hromadné B2B nástroje. */}
      <section>
        <div className="section-title">
          <h2>Hromadné skórovanie</h2>
          <span className="hint">zoznam IČO, max 50</span>
        </div>
        <div className="card">
          <p style={{ color: 'var(--slate)', fontSize: '0.83rem', margin: '0 0 0.6rem' }}>
            Vlož IČO oddelené čiarkou, medzerou alebo novým riadkom.
          </p>
          <textarea
            value={bulkIcosText}
            onChange={(e) => setBulkIcosText(e.target.value)}
            placeholder={'35790253\n31333532\n...'}
            rows={4}
            style={{
              ...inputStyle,
              display: 'block',
              width: '100%',
              margin: 0,
              resize: 'vertical',
            }}
          />
          <button
            type="button"
            onClick={submitBulkScore}
            style={{ ...buttonStyle, marginTop: '0.6rem' }}
          >
            Skórovať
          </button>
          {bulkLoading && (
            <p style={{ color: 'var(--slate)', fontSize: '0.85rem' }}>
              Počítam skóre pre {bulkIcosText.split(/[\s,;]+/).filter(Boolean).length} firiem...
            </p>
          )}
          {bulkError && (
            <p style={{ color: 'var(--risk-high)', fontSize: '0.85rem' }}>{bulkError}</p>
          )}
          {bulkResults && (
            <div className="overflow-x" style={{ marginTop: '0.9rem' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>IČO</th>
                    <th style={thStyle}>Firma</th>
                    <th style={thStyle}>Skóre</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkResults.map((r) => (
                    <tr key={r.ico}>
                      <td style={tdStyle} className="num">
                        <button
                          type="button"
                          onClick={() => navigate(`/firma/${r.ico}`)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--brand)',
                            cursor: 'pointer',
                            padding: 0,
                          }}
                        >
                          {r.ico}
                        </button>
                      </td>
                      <td style={tdStyle}>
                        {r.name ?? <span style={{ color: 'var(--risk-high)' }}>{r.error}</span>}
                      </td>
                      <td
                        style={{
                          ...tdStyle,
                          color: r.score != null ? riskColor(r.score) : undefined,
                        }}
                        className="num"
                      >
                        {r.score ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section>
        <div className="section-title">
          <h2>Porovnanie firiem</h2>
          <span className="hint">2-3 IČO vedľa seba</span>
        </div>
        <div className="card">
          <p style={{ color: 'var(--slate)', fontSize: '0.83rem', margin: '0 0 0.6rem' }}>
            Vlož 2-3 IČO oddelené čiarkou, medzerou alebo novým riadkom.
          </p>
          <textarea
            value={compareIcosText}
            onChange={(e) => setCompareIcosText(e.target.value)}
            placeholder={'35790253\n31333532'}
            rows={2}
            style={{
              ...inputStyle,
              display: 'block',
              width: '100%',
              margin: 0,
              resize: 'vertical',
            }}
          />
          <button
            type="button"
            onClick={() => void submitCompare()}
            style={{ ...buttonStyle, marginTop: '0.6rem' }}
          >
            Porovnať
          </button>
          {compareLoading && (
            <p style={{ color: 'var(--slate)', fontSize: '0.85rem' }}>Počítam porovnanie...</p>
          )}
          {compareError && (
            <p style={{ color: 'var(--risk-high)', fontSize: '0.85rem' }}>{compareError}</p>
          )}
          {compareResults && (
            <div className="overflow-x" style={{ marginTop: '0.9rem' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}></th>
                    {compareResults.map((r) => (
                      <th key={r.ico} style={thStyle}>
                        <button
                          type="button"
                          onClick={() => navigate(`/firma/${r.ico}`)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--brand)',
                            cursor: 'pointer',
                            padding: 0,
                            fontWeight: 600,
                          }}
                        >
                          {r.name ?? r.ico}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {compareResults.some((r) => r.error) && (
                    <tr>
                      <td style={tdStyle}>Chyba</td>
                      {compareResults.map((r) => (
                        <td key={r.ico} style={{ ...tdStyle, color: 'var(--risk-high)' }}>
                          {r.error ?? '—'}
                        </td>
                      ))}
                    </tr>
                  )}
                  <tr>
                    <td style={tdStyle}>Mesto</td>
                    {compareResults.map((r) => (
                      <td key={r.ico} style={tdStyle}>
                        {r.city ?? '—'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td style={tdStyle}>Rizikové skóre</td>
                    {compareResults.map((r) => (
                      <td
                        key={r.ico}
                        style={{
                          ...tdStyle,
                          color: r.riskScore != null ? riskColor(r.riskScore) : undefined,
                        }}
                        className="num"
                      >
                        {r.riskScore ?? '—'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td style={tdStyle}>Altman Z"</td>
                    {compareResults.map((r) => (
                      <td key={r.ico} style={tdStyle} className="num">
                        {r.altmanZScore ?? '—'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td style={tdStyle}>Tržby ({compareResults[0]?.latestPeriod ?? '—'})</td>
                    {compareResults.map((r) => (
                      <td key={r.ico} style={tdStyle} className="num">
                        {formatEur(r.revenue)}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td style={tdStyle}>Výsledok hosp.</td>
                    {compareResults.map((r) => (
                      <td
                        key={r.ico}
                        style={{
                          ...tdStyle,
                          color:
                            r.netIncome != null && r.netIncome < 0 ? 'var(--risk-high)' : undefined,
                        }}
                        className="num"
                      >
                        {formatEur(r.netIncome)}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td style={tdStyle}>Vlastné imanie</td>
                    {compareResults.map((r) => (
                      <td key={r.ico} style={tdStyle} className="num">
                        {formatEur(r.equity)}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
